import { ProcessTypeInput } from '@varo/graphql-schema-types';
import {
  isUserCancelled3DS,
  TabapayPartialAuthType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';

interface ErrorButton {
  buttonText: string;
}

interface CombinedErrorScreen {
  primaryButton?: ErrorButton;
  secondaryButton?: ErrorButton;
  messageHeadline: string;
  messageBody: string;
}

interface ErrorScreenWithProcess {
  processType?: ProcessTypeInput | string;
  isPartialAuth?: boolean;
}

export type ErrorScreenConfig = {
  errorScreen: CombinedErrorScreen;
  primaryButtonText: string;
  closeButtonHandler: () => void;
  primaryButtonHandler: () => void;
  secondaryButtonHandler?: (button?: ErrorButton) => void | Promise<void>;
  cancelTransferLoading?: boolean;
  variant?: 'standard' | 'eligibility';
  isProcessing?: boolean;
} | null;

interface CardFormErrorParams {
  combinedErrorScreen?: CombinedErrorScreen | null;
  errorScreenWeb?: ErrorScreenWithProcess | null;
  errorScreenThreeDs?: ErrorScreenWithProcess | null;
  setShouldRefreshCards: (value: boolean) => void;
  cancelTabapayTransfer: (
    params: { transactionId?: string },
    flowName: string,
  ) => void | Promise<void>;
  transactionId?: string;
  checkAndDeleteCard: () => void;
  translatedText: (key: string) => string;
  handleErrorPrimaryButtonOnPress: () => void;
  handleErrorButtonPress: (button?: ErrorButton) => void;
  cancelTransferLoading: boolean;
}

interface TransferErrorParams {
  combinedErrorScreen?: CombinedErrorScreen | null;
  errorThreeDsWeb?: ErrorScreenWithProcess | null;
  errorThreeDsHandler?: ErrorScreenWithProcess | null;
  cancelTabapayTransfer: (
    params: { transactionId?: string },
    flowName: string,
  ) => void | Promise<void>;
  transactionId?: string;
  checkAndDeleteCard: () => void;
  sourceName: string;
  translatedText: (key: string) => string;
  handleErrorButtonPress: (button?: ErrorButton) => Promise<void>;
  onPartialAuthClose: () => void;
}

interface EligibilityParams {
  isEligible: boolean;
  eligibleErrorScreen?: CombinedErrorScreen | null;
}

interface UseTabapayErrorHandlersParams {
  cardFormError: CardFormErrorParams;
  transferError: TransferErrorParams;
  eligibility: EligibilityParams;
  flowName: string;
  isProcessing?: boolean;
}

export const useTabapayErrorHandlers = ({
  cardFormError,
  transferError,
  eligibility,
  flowName,
  isProcessing = false,
}: UseTabapayErrorHandlersParams): ErrorScreenConfig => {
  const goToHomeScreenClicked = usePressableAnalytics({
    analyticsId: 'partialAuthErrorClosed',
    customAttributes: {
      flow_name: flowName,
      source_name: transferError.sourceName ?? undefined,
      cta:
        transferError.sourceName === 'partial_auth_transaction'
          ? 'Transfer'
          : undefined,
    },
  });

  const handleCardFormErrorClose = () => {
    cardFormError.setShouldRefreshCards(true);
    if (
      (cardFormError.combinedErrorScreen &&
        isUserCancelled3DS(cardFormError.combinedErrorScreen) &&
        cardFormError.errorScreenWeb?.processType ===
          ProcessTypeInput.Transaction) ||
      cardFormError.errorScreenThreeDs?.processType ===
        ProcessTypeInput.Transaction
    ) {
      void cardFormError.cancelTabapayTransfer(
        { transactionId: cardFormError.transactionId },
        flowName,
      );
    } else {
      if (cardFormError.combinedErrorScreen?.primaryButton?.buttonText) {
        navigateBack();
      } else {
        cardFormError.checkAndDeleteCard();
      }
    }
  };

  const handleTransferErrorClose = () => {
    const partialAuthStatuses = [
      TabapayPartialAuthType.PartialAuthTransaction.toString(),
      TabapayPartialAuthType.PartialAuthInsufficientFunds.toString(),
    ];

    const isPartialAuthClose = partialAuthStatuses.includes(
      transferError.sourceName,
    );

    if (isPartialAuthClose) {
      goToHomeScreenClicked();
      transferError.onPartialAuthClose();
    }

    const isTransactionWebError =
      transferError.errorThreeDsWeb?.processType ===
        ProcessTypeInput.Transaction &&
      (Boolean(
        transferError.combinedErrorScreen &&
        isUserCancelled3DS(transferError.combinedErrorScreen),
      ) ||
        transferError.errorThreeDsWeb?.isPartialAuth);

    if (
      isTransactionWebError ||
      transferError.errorThreeDsHandler?.processType ===
        ProcessTypeInput.Transaction
    ) {
      void transferError.cancelTabapayTransfer(
        { transactionId: transferError.transactionId },
        flowName,
      );
    } else {
      transferError.checkAndDeleteCard();
    }
  };

  const handleTransferPrimaryButtonPress = () => {
    if (transferError.combinedErrorScreen?.primaryButton?.buttonText) {
      void transferError.handleErrorButtonPress(
        transferError.combinedErrorScreen.primaryButton,
      );
    } else {
      transferError.checkAndDeleteCard();
    }
  };

  const handleEligibilityPrimaryButtonPress = () => {
    void transferError.handleErrorButtonPress(
      eligibility.eligibleErrorScreen?.primaryButton,
    );
  };

  if (cardFormError.combinedErrorScreen) {
    return {
      errorScreen: cardFormError.combinedErrorScreen,
      primaryButtonText: cardFormError.translatedText('closeButton'),
      closeButtonHandler: handleCardFormErrorClose,
      primaryButtonHandler: cardFormError.handleErrorPrimaryButtonOnPress,
      secondaryButtonHandler: cardFormError.handleErrorButtonPress,
      cancelTransferLoading: cardFormError.cancelTransferLoading,
      variant: 'standard',
      isProcessing,
    };
  }

  if (transferError.combinedErrorScreen) {
    return {
      errorScreen: transferError.combinedErrorScreen,
      primaryButtonText: transferError.translatedText('closeButton'),
      closeButtonHandler: handleTransferErrorClose,
      primaryButtonHandler: handleTransferPrimaryButtonPress,
      secondaryButtonHandler: transferError.handleErrorButtonPress,
      variant: 'standard',
      isProcessing,
    };
  }

  if (!eligibility.isEligible && eligibility.eligibleErrorScreen) {
    return {
      errorScreen: eligibility.eligibleErrorScreen,
      primaryButtonText: transferError.translatedText('closeButton'),
      closeButtonHandler: navigateBack,
      primaryButtonHandler: handleEligibilityPrimaryButtonPress,
      variant: 'eligibility',
      isProcessing,
    };
  }

  return null;
};
